import { useState } from 'react';
import { Button, FileDropzone } from '../../components/ui';
import { AssetImage } from '../../components/media/AssetImage';
import { useUploadMediaAsset } from '../../api/media-assets';
import { resizeImageForUpload } from '../../lib/resize-image';

/**
 * The product/service photo picker: drop or browse an image, it's resized
 * in the browser (<= 1280px JPEG) and uploaded right away, then shown as a
 * preview. The form saves only the returned image id (`imageAssetId`).
 * Customers see this photo on WhatsApp product cards (flow steps with
 * "Show photo & details" switched on).
 */
export function ProductImageField({
  value,
  onChange,
  error,
  label = 'Photo (optional)',
  hint = 'Shown to customers on WhatsApp when a flow step has "Show photo & details" on.',
  disabled = false,
}: {
  value: string | null;
  onChange: (assetId: string | null) => void;
  error?: string;
  label?: string;
  hint?: string;
  disabled?: boolean;
}) {
  const upload = useUploadMediaAsset();
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [picking, setPicking] = useState(!value);

  async function onFileSelected(file: File | null) {
    if (!file) return;
    setUploadError(null);
    try {
      const resized = await resizeImageForUpload(file);
      const asset = await upload.mutateAsync(resized);
      onChange(asset.id);
      setPicking(false);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Could not upload this image.');
    }
  }

  const message = uploadError ?? error;

  return (
    <div className="ne-field">
      <span className="ne-field__label">{label}</span>
      {value && !picking ? (
        <div
          style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'center', flexWrap: 'wrap' }}
        >
          <AssetImage assetId={value} alt="Product photo" size={120} />
          <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
            <Button
              type="button"
              variant="secondary"
              disabled={disabled}
              onClick={() => setPicking(true)}
            >
              Change photo
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={disabled}
              onClick={() => onChange(null)}
            >
              Remove photo
            </Button>
          </div>
        </div>
      ) : (
        <>
          <FileDropzone
            accept="image/jpeg,image/png,image/webp"
            file={null}
            onFileSelected={(f) => void onFileSelected(f)}
            placeholder={
              upload.isPending
                ? 'Uploading…'
                : 'Drag a photo here, or click to choose one (JPG or PNG).'
            }
          />
          {value ? (
            <div>
              <Button type="button" variant="secondary" onClick={() => setPicking(false)}>
                Keep current photo
              </Button>
            </div>
          ) : null}
        </>
      )}
      <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>
        {hint}
      </span>
      {message ? (
        <span className="ne-field__error" role="alert">
          {message}
        </span>
      ) : null}
    </div>
  );
}
